import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

/**
 * Public landing page. Coordinators deliberately have no button here -- they
 * sign in as staff and get an extra Admin tab once inside.
 */
const AUDIENCES = [
  {
    key: "student",
    label: "a student",
    detail: "Submit an expression of interest, upload your CV, and apply for internships.",
    icon: (
      <path d="M12 3 1 9l11 6 9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82Z" />
    ),
  },
  {
    key: "staff",
    label: "university staff",
    detail: "Act as a referee for students who have nominated you.",
    icon: (
      <path d="M20 6h-4V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2m-6-2v2h-4V4ZM4 19V8h16v11Z" />
    ),
  },
  {
    key: "industry",
    label: "an employer",
    detail: "Post internship positions and review student applicants.",
    icon: (
      <path d="M12 7V3H2v18h20V7zM6 19H4v-2h2zm0-4H4v-2h2zm0-4H4V9h2zm0-4H4V5h2zm4 12H8v-2h2zm0-4H8v-2h2zm0-4H8V9h2zm0-4H8V5h2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8zm-2-8h-2v2h2zm0 4h-2v2h2z" />
    ),
  },
] as const;

export default function Welcome() {
  const { data: config } = useQuery({
    queryKey: ["config"],
    queryFn: () => api.get<{ demoMode: boolean }>("/config"),
    staleTime: Infinity,
  });

  return (
    <div className="flex min-h-screen flex-col bg-uoa-blue">
      <header className="mx-auto w-full max-w-5xl px-6 pt-10">
        <div className="flex items-center gap-3 text-white">
          {/* TODO: replace with the supplied University of Auckland logo asset. */}
          <span aria-hidden className="grid h-11 w-11 place-items-center rounded bg-white text-lg font-bold text-uoa-blue">
            IS
          </span>
          <div>
            <p className="text-xl font-semibold tracking-tight">Intern-Seek</p>
            <p className="text-sm text-white/70">University of Auckland</p>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center px-6 py-16">
        <h1 className="text-5xl font-semibold tracking-tight text-white sm:text-6xl">I am&hellip;</h1>
        <p className="mt-4 max-w-xl text-lg text-white/80">
          Connecting University of Auckland students with internship opportunities across New Zealand.
        </p>

        <ul className="mt-12 grid gap-5 sm:grid-cols-3">
          {AUDIENCES.map((a) => (
            <li key={a.key}>
              <Link
                to={`/signin?as=${a.key}`}
                className="group flex h-full flex-col rounded-xl bg-white p-6 text-left shadow-lg
                           transition hover:-translate-y-1 hover:shadow-2xl focus-visible:-translate-y-1"
              >
                <svg viewBox="0 0 24 24" aria-hidden className="mb-4 h-9 w-9 fill-uoa-blue">
                  {a.icon}
                </svg>
                <span className="text-xl font-semibold text-uoa-blue-dark">{a.label}</span>
                <span className="mt-2 flex-1 text-sm text-uoa-grey">{a.detail}</span>
                <span className="mt-5 text-sm font-medium text-uoa-blue group-hover:underline">
                  Continue &rarr;
                </span>
              </Link>
            </li>
          ))}
        </ul>

        {config?.demoMode && (
          <p className="mt-10 text-sm text-white/70">
            <Link to="/demo" className="font-medium text-white underline">
              Explore the demo
            </Link>{" "}
            &mdash; sign in as any user with sample data, no password required.
          </p>
        )}
      </main>

      <footer className="mx-auto w-full max-w-5xl px-6 pb-8 text-sm text-white/60">
        Intern-Seek &middot; University of Auckland
      </footer>
    </div>
  );
}
